
import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useUpload } from "@/hooks/use-upload";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Image, Loader2, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type ContextType = { nursingHome: NursingHome };

export default function MediaPage() {
  const { nursingHome } = useOutletContext<ContextType>();
  const { uploadImage, isUploading, uploadError } = useUpload();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  
  // Demo images for now - these would come from the database in reality
  const [images, setImages] = useState<string[]>([
    nursingHome.main_image_url || "https://source.unsplash.com/featured/?nursing,home,elderly,care-1",
  ]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }
    
    try {
      // Use a simulated upload progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          return prev + 5;
        });
      }, 100);
      
      const uploadResult = await uploadImage(selectedFile);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (uploadResult) {
        toast.success("Image uploaded successfully");
        
        // Update the nursing home image URL in the database
        // Use brochure_url which is an existing field in the database schema
        const { error } = await supabase
          .from('nursing_homes')
          .update({
            brochure_url: uploadResult.url  // Using brochure_url instead of main_image_url
          })
          .eq('nh_slug', nursingHome.nh_slug);
        
        if (error) {
          throw error;
        }
        
        // Add to our images array for immediate display
        setImages(prev => [...prev, uploadResult.url]);
        
        // Reset state
        setSelectedFile(null);
        setTimeout(() => setUploadProgress(0), 1000);
      } else if (uploadError) {
        toast.error(`Upload failed: ${uploadError}`);
      }
    } catch (error) {
      console.error("Error in upload process:", error);
      toast.error("Failed to upload image");
      setUploadProgress(0);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Media Management</h1>
        <p className="text-muted-foreground">
          Upload and manage photos of your nursing home
        </p>
      </div>
      
      {/* Upload Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="photo-upload">Upload Photo</Label>
              <div className="mt-2 flex items-center">
                <Input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isUploading}
                  className="flex-1"
                />
                <Button 
                  onClick={handleUpload} 
                  disabled={!selectedFile || isUploading}
                  className="ml-4"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload
                    </>
                  )}
                </Button>
              </div>
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-grandpal-green h-2.5 rounded-full"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
              {selectedFile && (
                <p className="text-sm text-muted-foreground mt-2">
                  Selected: {selectedFile.name}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Photos Gallery */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Photo Gallery</h2>
        
        {images.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {images.map((imageUrl, index) => (
              <Card key={index} className="overflow-hidden">
                <div className="aspect-video relative group">
                  <img 
                    src={imageUrl.startsWith('http') ? imageUrl : `/${imageUrl}`}
                    alt={`${nursingHome.center_name} - Photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button variant="secondary" size="sm" className="mr-2">
                      <Image className="h-4 w-4 mr-2" />
                      View
                    </Button>
                  </div>
                </div>
                <div className="p-2 text-sm text-muted-foreground">
                  {index === 0 ? "Main Image" : `Image ${index + 1}`}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-6 text-center">
            <p className="text-muted-foreground">No photos have been uploaded yet.</p>
            <p className="text-sm mt-2">Upload photos to showcase your nursing home.</p>
          </Card>
        )}
      </div>
    </div>
  );
}
